import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

interface AdminUser {
  id: string;
  auth_user_id: string;
  email: string;
  full_name: string;
  user_name: string;
  role: string;
  region_id: string | null;
  is_active: boolean;
}

const resolveAdminFromSession = (session: Session | null): AdminUser | null => {
  const user = session?.user;
  if (!user) return null;
  const metadata = user.user_metadata || {};
  const appMetadata = user.app_metadata || {};
  const role = metadata.role || appMetadata.role;
  if (!role) return null;
  const email = user.email || metadata.email || "";
  const fullName = metadata.full_name || metadata.name || email.split("@")[0] || "Admin";

  return {
    id: user.id,
    auth_user_id: user.id,
    email,
    full_name: fullName,
    user_name: metadata.user_name || email,
    role,
    region_id: metadata.region_id || appMetadata.region_id || null,
    is_active: true,
  };
};

export const useAdminAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let requestId = 0;
    let loadedAuthUserId: string | null = null;
    let pendingAuthUserId: string | null = null;

    const applySessionState = async (nextSession: Session | null, opts?: { force?: boolean }) => {
      if (!mounted) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      const authUserId = nextSession?.user?.id ?? null;
      if (!authUserId) {
        loadedAuthUserId = null;
        pendingAuthUserId = null;
        setAdminUser(null);
        setLoading(false);
        return;
      }

      // Skip re-fetch if user has not actually changed (TOKEN_REFRESHED etc.).
      // This prevents the admin dashboard from unmounting on tab focus.
      if (authUserId === loadedAuthUserId && !opts?.force) {
        setLoading(false);
        return;
      }

      // Supabase can emit INITIAL_SESSION and SIGNED_IN back-to-back for the
      // same session. Never let the duplicate cancel the in-flight lookup —
      // that used to leave adminUser null with loading=false.
      if (authUserId === pendingAuthUserId && !opts?.force) {
        return;
      }

      pendingAuthUserId = authUserId;
      const currentRequestId = ++requestId;

      // Only trigger a loading state on initial load; otherwise keep existing
      // adminUser mounted while we quietly refresh in the background.
      setAdminUser((prev) => {
        if (!prev) setLoading(true);
        return prev;
      });

      const timeoutFallback = new Promise<AdminUser | null>((resolve) => {
        window.setTimeout(() => resolve(resolveAdminFromSession(nextSession)), 8000);
      });
      const nextAdminUser = await Promise.race([fetchAdminUser(authUserId), timeoutFallback]);

      if (!mounted || currentRequestId !== requestId) return;

      loadedAuthUserId = authUserId;
      pendingAuthUserId = null;
      setAdminUser(nextAdminUser);
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      void applySessionState(session, { force: true });
    }).catch((error) => {
      console.error("[admin-auth] Initial session lookup failed", error);
      if (mounted) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      void applySessionState(session);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchAdminUser = async (authUserId: string): Promise<AdminUser | null> => {
    if (!authUserId || typeof authUserId !== 'string') {
      console.warn('[admin-auth] Cannot fetch admin user without a valid auth user id', { authUserId });
      return null;
    }
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching admin user:', error);
        return null;
      }
      if (!data) {
        console.warn('[admin-auth] No active admin row for authenticated user', { authUserId });
        return null;
      }
      ['id', 'email', 'role'].forEach((field) => {
        if (!(data as any)?.[field]) console.warn('[admin-auth] Admin row missing field', { field, authUserId });
      });
      return {
        ...data,
        email: data.email || '',
        full_name: data.full_name || '',
        user_name: data.user_name || '',
        role: data.role || '',
        region_id: data.region_id || null,
        is_active: data.is_active === true,
      } as AdminUser;
    } catch (error) {
      console.error('Error in fetchAdminUser:', error);
      return null;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setAdminUser(null);
  };

  const isAuthenticated = !!session && !!adminUser;
  const isAdmin = isAuthenticated && adminUser?.is_active;
  const isGlobalAdmin = isAdmin && adminUser?.role === 'global_admin';
  const isPresident = isAdmin && adminUser?.role === 'president';
  const isRegionalAdmin = isAdmin && adminUser?.role === 'regional_admin';
  const isKitAdmin = isAdmin && adminUser?.role === 'kit_admin';

  return {
    user,
    session,
    adminUser,
    loading,
    signOut,
    isAuthenticated,
    isAdmin,
    isGlobalAdmin,
    isPresident,
    isRegionalAdmin,
    isKitAdmin
  };
};
